import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProjectFilters = ({ onFiltersChange, onCreateProject }) => {
  const [filters, setFilters] = useState({
    search: '',
    client: '',
    status: '',
    startDate: '',
    endDate: '',
    technician: ''
  });

  const clientOptions = [
    { value: '', label: 'Todos os Clientes' },
    { value: 'abc-corp', label: 'ABC Corporation' },
    { value: 'xyz-ltd', label: 'XYZ Limitada' },
    { value: 'tech-solutions', label: 'Tech Solutions Brasil' },
    { value: 'global-services', label: 'Global Services Ltda' },
    { value: 'innovate-co', label: 'Innovate & Co' }
  ];

  const statusOptions = [
    { value: '', label: 'Todos os Status' },
    { value: 'planning', label: 'Planejamento' },
    { value: 'active', label: 'Ativo' },
    { value: 'on-hold', label: 'Pausado' },
    { value: 'completed', label: 'Concluído' },
    { value: 'cancelled', label: 'Cancelado' }
  ];

  const technicianOptions = [
    { value: '', label: 'Todos os Técnicos' },
    { value: 'joao-silva', label: 'João Silva' },
    { value: 'maria-santos', label: 'Maria Santos' },
    { value: 'carlos-oliveira', label: 'Carlos Oliveira' },
    { value: 'ana-costa', label: 'Ana Costa' },
    { value: 'pedro-ferreira', label: 'Pedro Ferreira' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      client: '',
      status: '',
      startDate: '',
      endDate: '',
      technician: ''
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-1">
            Gerenciar Projetos
          </h2>
          <p className="text-sm text-muted-foreground">
            Controle e monitore todos os projetos de consultoria
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              iconName="X"
              iconPosition="left"
            >
              Limpar Filtros
            </Button>
          )}
          <Button
            variant="default"
            onClick={onCreateProject}
            iconName="Plus"
            iconPosition="left"
          >
            Novo Projeto
          </Button>
        </div>
      </div>
      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Search */}
        <div className="xl:col-span-2">
          <Input
            type="search"
            placeholder="Buscar projetos..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Client Filter */}
        <Select
          placeholder="Cliente"
          options={clientOptions}
          value={filters?.client}
          onChange={(value) => handleFilterChange('client', value)}
        />

        {/* Status Filter */}
        <Select
          placeholder="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
        />

        {/* Start Date */}
        <Input
          type="date"
          label="Data Início"
          value={filters?.startDate}
          onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
        />

        {/* End Date */}
        <Input
          type="date"
          label="Data Fim"
          value={filters?.endDate}
          onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
        />
      </div>
      {/* Advanced Filters Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
        {/* Technician Filter */}
        <Select
          placeholder="Técnico Responsável"
          options={technicianOptions}
          value={filters?.technician}
          onChange={(value) => handleFilterChange('technician', value)}
        />

        {/* Quick Filter Buttons */}
        <div className="flex items-center space-x-2 lg:col-span-3">
          <span className="text-sm text-muted-foreground mr-2">Filtros rápidos:</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleFilterChange('status', 'active')}
          >
            Projetos Ativos
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleFilterChange('status', 'planning')}
          >
            Em Planejamento
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const today = new Date()?.toISOString()?.split('T')?.[0];
              handleFilterChange('startDate', today);
            }}
          >
            Iniciados Hoje
          </Button>
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-border">
          <span className="text-sm text-muted-foreground">Filtros ativos:</span>
          {filters?.search && (
            <div className="flex items-center bg-primary/10 text-primary px-2 py-1 rounded-md text-sm">
              <Icon name="Search" size={14} className="mr-1" />
              {filters?.search}
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 ml-1"
                onClick={() => handleFilterChange('search', '')}
              >
                <Icon name="X" size={12} />
              </Button>
            </div>
          )}
          {filters?.client && (
            <div className="flex items-center bg-success/10 text-success px-2 py-1 rounded-md text-sm">
              <Icon name="Building" size={14} className="mr-1" />
              {clientOptions?.find(opt => opt?.value === filters?.client)?.label}
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 ml-1"
                onClick={() => handleFilterChange('client', '')}
              >
                <Icon name="X" size={12} />
              </Button>
            </div>
          )}
          {filters?.status && (
            <div className="flex items-center bg-accent/10 text-accent px-2 py-1 rounded-md text-sm">
              <Icon name="Tag" size={14} className="mr-1" />
              {statusOptions?.find(opt => opt?.value === filters?.status)?.label}
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 ml-1"
                onClick={() => handleFilterChange('status', '')}
              >
                <Icon name="X" size={12} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectFilters;